import { Link } from "@tanstack/react-router";
import { motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";
import { openReport, setUi } from "../../lib/uiStore";
import { applyUpdate, canCheckUpdate, checkForUpdate } from "../../pwa";
import { BrandMark } from "../../ui/Brand";
import { BugIcon, CheckIcon, CopyIcon, DownloadIcon, HelpIcon, InfoIcon, MenuIcon, RetryIcon, SparkIcon } from "../../ui/icons";
import { IconButton } from "../../ui/IconButton";
import { toast } from "../../ui/toast";
import { useMe } from "../chat/queries";
import { useChangelog } from "../whatsnew/seen";
import { useChatLayout } from "../chat/layoutContext";

const ease = [0.22, 1, 0.36, 1] as const;

/** Строки «О программе»: их же копирует кнопка «Скопировать сведения» для поддержки. */
export function aboutRows(o: { version: string | undefined; build: string; host: string; email: string | undefined; browser: string }): [string, string][] {
  return [
    ["Версия", o.version ?? "—"],
    ["Сборка", o.build],
    ["Где открыт", `Веб, ${o.browser}`],
    ["Сервер", o.host],
    ...(o.email ? ([["Вы вошли как", o.email]] as [string, string][]) : []),
  ];
}

export function browserName(ua: string): string {
  if (/Edg\//.test(ua)) return "Edge";
  if (/YaBrowser\//.test(ua)) return "Яндекс Браузер";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Safari\//.test(ua)) return "Safari";
  return "браузер";
}

type Update = "idle" | "checking" | "latest" | "ready";

/** Обновления: сайт обновляется сам; кнопка — проверить сейчас и подключить новую версию сразу. */
export function UpdateBlock({ cls }: { cls: string }) {
  const [state, setState] = useState<Update>("idle");
  const check = async () => {
    setState("checking");
    setState((await checkForUpdate()) ? "ready" : "latest");
  };
  return (
    <section aria-labelledby="about-updates" className="mt-6 rounded-card border border-line bg-surface p-4">
      <h3 id="about-updates" className="text-sm font-semibold">
        Обновления
      </h3>
      <p className="mt-1 text-[13px] leading-[18px] text-sub">
        Веб-версия обновляется сама: новая версия подключается, когда вы переключаетесь на другое окно.
      </p>
      {canCheckUpdate() && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {state === "ready" ? (
            <button type="button" onClick={applyUpdate} className="pressable h-10 rounded-control bg-accent-grad hover-accent-grad px-4 text-sm font-semibold text-white">
              Обновить сейчас
            </button>
          ) : (
            <button type="button" onClick={() => void check()} disabled={state === "checking"} className={cls}>
              <RetryIcon />
              {state === "checking" ? "Проверяю…" : "Проверить обновления"}
            </button>
          )}
          <span role="status" className="text-[13px] text-sub">
            {state === "latest" ? "У вас последняя версия" : state === "ready" ? "Новая версия скачана" : ""}
          </span>
        </div>
      )}
    </section>
  );
}

/** «О программе»: название, версия и сборка, где открыт, сервер — то, что спрашивает поддержка. */
export function About() {
  const { layout, setDrawerOpen } = useChatLayout();
  const reduce = useReducedMotion();
  const me = useMe();
  const version = useChangelog().data?.[0]?.version;
  const [copied, setCopied] = useState(false);
  const rows = aboutRows({ version, build: __APP_VERSION__, host: location.host, email: me?.email, browser: browserName(navigator.userAgent) });

  useEffect(() => {
    document.title = "О программе · Ассистент";
    return () => {
      document.title = "Ассистент";
    };
  }, []);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(["Ассистент ОМС", ...rows.map(([k, v]) => `${k}: ${v}`)].join("\n"));
      setCopied(true);
      toast("Сведения скопированы");
    } catch {
      toast("Не удалось скопировать");
    }
  };

  const link = "pressable flex h-11 items-center gap-2.5 rounded-control border border-line bg-surface px-4 text-sm font-medium hover:bg-bg [&_svg]:size-4 [&_svg]:text-sub";

  return (
    <>
      <header className="flex h-14 flex-none items-center gap-1 border-b border-line-soft pr-1.5 pl-1 md:h-[60px] md:gap-2.5 md:pr-3 md:pl-5">
        {layout === "phone" && (
          <IconButton label="Открыть список чатов" onClick={() => setDrawerOpen(true)} tip={false}>
            <MenuIcon />
          </IconButton>
        )}
        <h1 className="min-w-0 truncate text-base leading-[22px] font-semibold">О программе</h1>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.36, ease }}
          className="mx-auto max-w-[560px] px-4 pt-8 pb-10 md:px-6 md:pt-12"
        >
          <div className="flex flex-col items-center text-center">
            <BrandMark className="size-16" />
            <h2 className="mt-4 text-[22px] leading-[28px] font-bold tracking-[-0.02em]">Ассистент ОМС</h2>
            <p className="mt-1 text-[15px] leading-[22px] text-sub">Корпоративный ИИ-ассистент для сотрудников ОМС</p>
          </div>

          <dl data-testid="about-rows" className="mt-8 divide-y divide-line-soft rounded-card border border-line bg-surface">
            {rows.map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-4 px-4 py-3 text-sm">
                <dt className="text-sub">{k}</dt>
                <dd className="tabular min-w-0 text-right font-medium break-all">{v}</dd>
              </div>
            ))}
          </dl>

          <button type="button" onClick={() => void copy()} className={`${link} mt-3 w-full justify-center`}>
            {copied ? <CheckIcon /> : <CopyIcon />}
            {copied ? "Скопировано" : "Скопировать сведения"}
          </button>
          <p className="mt-2 text-center text-[13px] leading-[18px] text-sub">Пригодится, если поддержка спросит, какая у вас версия.</p>

          <UpdateBlock cls={link} />

          <div className="mt-6 grid gap-2">
            <Link to="/whats-new" className={link}>
              <SparkIcon />
              Что нового
            </Link>
            <Link to="/download" className={link}>
              <DownloadIcon />
              Приложения для других устройств
            </Link>
            <Link to="/help" className={link}>
              <HelpIcon />
              Справка
            </Link>
            <button type="button" onClick={() => openReport()} className={`${link} text-left`}>
              <BugIcon />
              Сообщить об ошибке
            </button>
          </div>

          <p className="mt-8 text-center text-[13px] leading-[18px] text-sub">© {new Date().getFullYear()} ОМС. Для сотрудников компании.</p>
        </motion.div>
      </div>
    </>
  );
}

/** «О программе» в настройках: главное для поддержки и проверка обновлений; полная страница — по ссылке. */
export function AboutSettings() {
  const me = useMe();
  const version = useChangelog().data?.[0]?.version;
  const rows = aboutRows({ version, build: __APP_VERSION__, host: location.host, email: me?.email, browser: browserName(navigator.userAgent) });
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(["Ассистент ОМС", ...rows.map(([k, v]) => `${k}: ${v}`)].join("\n"));
      toast("Сведения скопированы");
    } catch {
      toast("Не удалось скопировать");
    }
  };
  const link = "pressable flex h-11 items-center gap-2.5 rounded-control border border-line bg-surface px-4 text-sm font-medium hover:bg-bg [&_svg]:size-4 [&_svg]:text-sub";
  return (
    <>
      <dl className="divide-y divide-line-soft rounded-[16px] border border-edge">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-4 px-4 py-3 text-sm">
            <dt className="text-sub">{k}</dt>
            <dd className="tabular min-w-0 text-right font-medium break-all">{v}</dd>
          </div>
        ))}
      </dl>
      <button type="button" onClick={() => void copy()} className={`${link} mt-3 w-full justify-center`}>
        <CopyIcon />
        Скопировать сведения для поддержки
      </button>
      <UpdateBlock cls={link} />
      <Link to="/about" onClick={() => setUi({ settings: null })} className={`${link} mt-3`}>
        <InfoIcon />
        Открыть страницу «О программе»
      </Link>
    </>
  );
}
